import React from "react";
import {
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useFormikContext } from "formik";

import FieldGroup from "../../../shared/FieldGroup";
import SectionCard from "../../../shared/SectionCard";
import type { TargetFormValues } from "../TargetBuilderPanel.types";
import {
  IMPLEMENTATION_SETTING_OPTIONS,
  PHASE_OPTIONS,
  PRIMARY_IMPLEMENTER_OPTIONS,
  STATUS_OPTIONS,
  TARGET_TYPE_OPTIONS,
} from "../TargetBuilderPanel.types";

interface TargetFormSection1Props {
  /**
   * Active behavior-reduction goals for the current client.
   * Surfaced in the FCR dropdown when the target is marked as a replacement behavior.
   */
  replacementGoalOptions?: Array<{ id: number; title: string }>;
}

const TargetFormSection1: React.FC<TargetFormSection1Props> = ({ replacementGoalOptions = [] }) => {
  const { values, errors, touched, handleChange, handleBlur, setFieldValue } =
    useFormikContext<TargetFormValues>();

  return (
    <SectionCard
      id="target-section-overview"
      title="Section 1 — Target overview"
      description="Basic target identification, type, phase, and assignment"
    >
      <FieldGroup
        label="Target Name"
        description="A clear, concise name for this target"
        required
        error={touched.title && errors.title}
      >
        <TextField
          size="small"
          fullWidth
          name="title"
          placeholder="e.g., 'Request break using AAC device'"
          value={values.title}
          onChange={handleChange}
          onBlur={handleBlur}
          aria-label="Target name"
        />
      </FieldGroup>

      <FieldGroup
        label="Target Type"
        description="Inherited from parent goal — confirm or change"
        required
      >
        <RadioGroup
          row
          name="target_type"
          value={values.target_type}
          onChange={(_, v) => {
            const next = v as TargetFormValues["target_type"];
            setFieldValue("target_type", next);
            // FCR only applies to skill-acquisition — clear stale FCR state when
            // switching to behavior-reduction so it doesn't leak into the payload.
            if (next === "behavior-reduction") {
              setFieldValue("is_replacement_behavior", false);
              setFieldValue("replacement_for_goal_id", "");
              setFieldValue("eo_conditions", "");
              setFieldValue("prompting_strategy_under_eo", "");
              setFieldValue("competing_response_considerations", "");
            }
            // Intervention strategies are scoped to target_type — clear them
            // so stale options don't bleed across types.
            if (values.teaching_procedure) {
              setFieldValue("teaching_procedure", {
                ...values.teaching_procedure,
                intervention_strategies: [],
              });
            }
          }}
          aria-label="Target type"
          sx={{ gap: "12px" }}
        >
          {TARGET_TYPE_OPTIONS.map((opt) => (
            <FormControlLabel
              key={opt.value}
              value={opt.value}
              control={<Radio size="small" />}
              label={opt.label}
            />
          ))}
        </RadioGroup>
      </FieldGroup>

      {values.target_type === "skill-acquisition" && (
        <FieldGroup
          label="Is this a replacement behavior / FCR?"
          description="Is this target a replacement behavior or functional communication response?"
        >
          <RadioGroup
            row
            value={values.is_replacement_behavior ? "yes" : "no"}
            onChange={(_, v) => setFieldValue("is_replacement_behavior", v === "yes")}
            aria-label="Is this a replacement behavior"
          >
            <FormControlLabel value="no" control={<Radio size="small" />} label="No" />
            <FormControlLabel value="yes" control={<Radio size="small" />} label="Yes" />
          </RadioGroup>
        </FieldGroup>
      )}

      {values.target_type === "skill-acquisition" && values.is_replacement_behavior && (
        <>
          <FieldGroup
            label="Replacement For"
            description="Which behavior reduction goal does this replace?"
            required={replacementGoalOptions.length > 0}
            error={touched.replacement_for_goal_id && (errors.replacement_for_goal_id as string)}
          >
            {replacementGoalOptions.length > 0 ? (
              <Select
                size="small"
                fullWidth
                name="replacement_for_goal_id"
                value={values.replacement_for_goal_id}
                onChange={handleChange}
                onBlur={handleBlur}
                displayEmpty
                aria-label="Replacement for goal"
                renderValue={(v) =>
                  v ? (
                    replacementGoalOptions.find((g) => g.id === v)?.title
                  ) : (
                    <Typography variant="body2" color="text.disabled">
                      Select a goal...
                    </Typography>
                  )
                }
              >
                {replacementGoalOptions.map((g) => (
                  <MenuItem key={g.id} value={g.id}>
                    {g.title}
                  </MenuItem>
                ))}
              </Select>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No behavior reduction goals found for this client.
              </Typography>
            )}
          </FieldGroup>

          <FieldGroup
            label="EO Conditions"
            description="Establishing operations — under what conditions should this response be used?"
            required
            error={touched.eo_conditions && errors.eo_conditions}
          >
            <TextField
              multiline
              minRows={3}
              fullWidth
              placeholder="e.g., When demand or transition is presented."
              name="eo_conditions"
              value={values.eo_conditions}
              onChange={handleChange}
              onBlur={handleBlur}
            />
          </FieldGroup>

          <FieldGroup
            label="Prompting Strategy Under EO"
            description="How to prompt the replacement behavior during motivating conditions"
            required
            error={touched.prompting_strategy_under_eo && errors.prompting_strategy_under_eo}
          >
            <TextField
              multiline
              minRows={3}
              fullWidth
              placeholder="e.g., Gesture or verbal prompt to AAC device if no spontaneous communication occurs within 5 seconds."
              name="prompting_strategy_under_eo"
              value={values.prompting_strategy_under_eo}
              onChange={handleChange}
              onBlur={handleBlur}
            />
          </FieldGroup>

          <FieldGroup
            label="Competing Response Considerations"
            description="How does this skill compete with the reduction behavior"
            required
            error={
              touched.competing_response_considerations && errors.competing_response_considerations
            }
          >
            <TextField
              multiline
              minRows={3}
              fullWidth
              placeholder="e.g., Replace tantrum behavior with this skill to achieve the same function."
              name="competing_response_considerations"
              value={values.competing_response_considerations}
              onChange={handleChange}
              onBlur={handleBlur}
            />
          </FieldGroup>
        </>
      )}

      <Stack direction="row" gap={4}>
        <FieldGroup label="Phase" description="Clinical progression stage" required>
          <Select
            size="small"
            fullWidth
            name="phase"
            value={values.phase}
            onChange={handleChange}
            onBlur={handleBlur}
            aria-label="Phase"
            sx={{ minWidth: 200 }}
          >
            {PHASE_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FieldGroup>

        <FieldGroup label="Status" description="Operational state" required>
          <Select
            size="small"
            fullWidth
            name="status"
            value={values.status}
            onChange={handleChange}
            onBlur={handleBlur}
            aria-label="Status"
            sx={{ minWidth: 200 }}
          >
            {STATUS_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FieldGroup>
      </Stack>

      <FieldGroup
        label="Primary Implementer"
        description="Who will primarily implement this target"
        required
      >
        <RadioGroup
          row
          name="primary_implementer"
          value={values.primary_implementer}
          onChange={(_, v) => setFieldValue("primary_implementer", v)}
          aria-label="Primary implementer"
        >
          {PRIMARY_IMPLEMENTER_OPTIONS.map((opt) => (
            <FormControlLabel
              key={opt.value}
              value={opt.value}
              control={<Radio size="small" />}
              label={opt.label}
            />
          ))}
        </RadioGroup>
      </FieldGroup>

      <FieldGroup
        label="Implementation Setting"
        description="Where will this target be implemented?"
        required
      >
        <RadioGroup
          row
          name="implementation_setting"
          value={values.implementation_setting}
          onChange={(_, v) => setFieldValue("implementation_setting", v)}
          aria-label="Implementation setting"
        >
          {IMPLEMENTATION_SETTING_OPTIONS.map((opt) => (
            <FormControlLabel
              key={opt.value}
              value={opt.value}
              control={<Radio size="small" />}
              label={opt.label}
            />
          ))}
        </RadioGroup>
      </FieldGroup>
    </SectionCard>
  );
};

export default TargetFormSection1;
